import styles from './area-panel.module.scss';
import { Add, TrashCan } from '@carbon/react/icons';
import {
  Button,
  InlineLoading,
  Modal,
  Search,
  SkeletonPlaceholder,
  Layer,
} from '@carbon/react';
import { TooltipIcon } from 'components/tooltip-icon/tooltip-icon';
import { LaunchLink } from 'components/launch-link';
import { useScenarioForm } from './contexts';
import { atom, useAtom, useAtomValue } from 'jotai';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { createResourceAtom } from 'lib/atoms';
import { capitalize, debounce } from 'lodash-es';
import classNames from 'classnames';
import {
  AreaConfigResponse,
  AreaConfigsResponse,
} from 'types/dca-scenarios-api/responses';
import { INITIAL_CONFIG } from './default-values';
import { deleteAreaConfigAtom } from './atoms';
import { DeleteModal } from './delete-modal';
import { useParams } from 'react-router-dom';

interface AreaPanelProps {
  resourceUrl?: string;
}

const FALLBACK_ATOM = atom(null);

interface AreaPanelProps {
  resourceUrl?: string;
  setIsAddScenarioNameModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

interface DeleteConfigModalState {
  open: boolean;
  href: string | null;
  name: string | null;
}

interface PreventAddNewState {
  open: boolean;
  id: string | null;
}

export const AreaPanel = ({
  resourceUrl,
  setIsAddScenarioNameModalOpen,
}: AreaPanelProps) => {
  const {
    form,
    isFormModified,
    resetConfigId,
    setSelectedConfigId,
    setConfigs,
    configs,
    selectedConfig,
    handleSubmitForm,
    setDiscardCount,
  } = useScenarioForm();
  const { id: scenarioId = null } = useParams<{ id?: string }>();
  const [searchText, setSearchText] = useState<string>('');
  const [filteredAreaConfigs, setFilteredAreaConfigs] = useState<
    AreaConfigResponse[] | null
  >(null);
  const [preventAddNewState, setPreventAddNewState] =
    useState<PreventAddNewState>({
      open: false,
      id: null,
    });
  const [deleteConfigModalState, setDeleteConfigModalState] =
    useState<DeleteConfigModalState>({
      open: false,

      href: null,
      name: null,
    });
  const [deleteAreaConfigState, runDeleteAreaConfig] =
    useAtom(deleteAreaConfigAtom);

  const areaConfigsAtom = useMemo(() => {
    if (!resourceUrl) return FALLBACK_ATOM;

    return createResourceAtom<AreaConfigsResponse>(resourceUrl);
  }, [resourceUrl]);

  const areaConfigsResponse = useAtomValue(areaConfigsAtom);

  const globalConfigs = useMemo(
    () =>
      configs.filter(
        (config) => config.areaGeometryId === null && config.type === 'global'
      ),
    [configs]
  );

  const areaConfigs = useMemo(
    () =>
      configs.filter(
        (config) => config.areaGeometryId !== null || config.type === 'area'
      ),
    [configs]
  );

  useEffect(() => {
    if (areaConfigsResponse === null) {
      const newId = 'local';
      setConfigs([{ ...INITIAL_CONFIG, id: newId, type: 'global' }]);
      setSelectedConfigId('local');
    } else if (
      areaConfigsResponse.data &&
      'results' in areaConfigsResponse.data
    ) {
      setConfigs(
        areaConfigsResponse.data.results.map((config) => ({
          ...config,
          type: config.areaGeometryId === null ? 'global' : 'area',
        }))
      );
      const selected = areaConfigsResponse.data.results[0];
      setSelectedConfigId(selected.id);
    }
  }, [areaConfigsResponse, setConfigs, setSelectedConfigId]);

  const debouncedFilteredOptions = useMemo(() => {
    return debounce((text: string) => {
      if (!text) return setFilteredAreaConfigs(null);

      const filtered = areaConfigs.filter((config) => {
        const name = `${config.areaGeometryName} / ${config.developmentType}${
          config.formation ? ` / ${config.formation}` : ''
        }`;
        return name.toLowerCase().includes(text.toLowerCase());
      });

      setFilteredAreaConfigs(filtered);
    }, 300);
  }, [areaConfigs]);

  const handleAddNewConfig = () => {
    const hasUnsaved = configs.some((config) => !config.href);

    if (hasUnsaved) return;

    const newId = 'local';
    const newConfig = {
      ...INITIAL_CONFIG,
      id: 'local',
      name: 'New Area Config',
      type: 'area',
    };
    setConfigs((prev) => [...prev, { ...newConfig, id: newId, type: 'area' }]);
    setSelectedConfigId(newId);
    setDiscardCount((prev) => prev + 1);
  };

  const handleDeleteConfig = (href: string) => {
    runDeleteAreaConfig({
      url: href,
      onSuccess: () => {
        setConfigs((prev) => prev.filter((config) => config.href !== href));
        setDeleteConfigModalState({ open: false, href: null, name: null });
      },
    });
  };

  const handleSwitchConfig = (id: string, isModified: boolean) => {
    if (id === selectedConfig?.id) return;

    if (isModified) {
      setPreventAddNewState({ open: true, id: id });
    } else {
      resetConfigId(id);
      setDiscardCount((prev) => prev + 1);

      const isLocalConfig = selectedConfig?.id === 'local';
      if (isLocalConfig) {
        setConfigs((prev) => prev.filter((c) => c.id !== 'local'));
      }
    }
  };

  if (areaConfigsResponse && areaConfigsResponse.loading)
    return <AreaPanelSkeleton />;

  return (
    <div>
      <form.Subscribe
        selector={(state) => {
          const isModified = isFormModified(state.values);

          return isModified;
        }}
      >
        {(isModified) => (
          <>
            <div className={styles.configsListContainer}>
              {globalConfigs.map((config, index) => {
                const name = `Global / ${capitalize(config.developmentType)}`;

                return (
                  <ConfigItem
                    key={config.id || index}
                    name={name}
                    id={config.id}
                    hideDelete={!config.href || globalConfigs.length === 1}
                    isActive={config.id === selectedConfig?.id}
                    isDeleting={
                      deleteAreaConfigState?.loading &&
                      deleteConfigModalState.href === config.href
                    }
                    onDelete={() => {
                      setDeleteConfigModalState({
                        open: true,
                        href: config.href,
                        name: name,
                      });
                    }}
                    onClick={() => handleSwitchConfig(config.id, isModified)}
                  />
                );
              })}
            </div>

            <div className={styles.searchConfigsContainer}>
              <Layer>
                <Search
                  size="md"
                  value={searchText}
                  labelText="Search"
                  placeholder="Search input text"
                  onChange={(e) => {
                    const text = e.target.value;
                    setSearchText(text);
                    debouncedFilteredOptions(text);
                  }}
                />
              </Layer>

              <Button
                kind="primary"
                size="md"
                renderIcon={Add}
                disabled={configs.some((config) => !config.href)}
                onClick={() => {
                  if (isModified) {
                    setPreventAddNewState({ open: true, id: null });
                  } else {
                    handleAddNewConfig();
                  }
                }}
              >
                Add
              </Button>
            </div>

            <div className={styles.configsListContainer}>
              {(filteredAreaConfigs || areaConfigs).map((config, index) => {
                const name = `${
                  config.areaGeometryName || 'New Area Config'
                } / ${capitalize(config.developmentType)}${
                  config.formation ? ` / ${config.formation}` : ''
                }`;

                return (
                  <ConfigItem
                    key={config.id || index}
                    name={name}
                    id={config.id}
                    hideDelete={!config.areaGeometryId || !config.href}
                    isActive={config.id === selectedConfig?.id}
                    isDeleting={
                      deleteAreaConfigState?.loading &&
                      deleteConfigModalState.href === config.href
                    }
                    areaGeometryId={config.areaGeometryId}
                    onDelete={() => {
                      setDeleteConfigModalState({
                        open: true,
                        href: config.href,
                        name: name,
                      });
                    }}
                    onClick={() => handleSwitchConfig(config.id, isModified)}
                  />
                );
              })}
            </div>
          </>
        )}
      </form.Subscribe>
      {preventAddNewState.open && (
        <PreventAddNewModal
          newConfigId={preventAddNewState.id}
          onClose={() => setPreventAddNewState({ open: false, id: null })}
          onSave={() => {
            if (!scenarioId) {
              setIsAddScenarioNameModalOpen(true);
            } else {
              handleSubmitForm({
                dcaScenarioId: scenarioId,
                areaConfigId: selectedConfig?.href ? selectedConfig.id : null,
              });
            }
            setPreventAddNewState({ open: false, id: null });
          }}
        />
      )}
      <DeleteModal
        open={deleteConfigModalState.open}
        onClose={() => {
          setDeleteConfigModalState({ open: false, href: null, name: null });
        }}
        onDelete={() => {
          if (!deleteConfigModalState.href) return;
          handleDeleteConfig(deleteConfigModalState.href);
        }}
        title="Delete Area Config"
        description={`Are you sure you want to delete the "${deleteConfigModalState.name}" area config?`}
      />
    </div>
  );
};

interface ConfigItemProps {
  name: string;
  id: string;
  hideDelete?: boolean;
  onDelete: () => void;
  onClick: () => void;
  isActive?: boolean;
  isDeleting?: boolean;
  areaGeometryId?: string | null;
}

const ConfigItem = ({
  name,
  hideDelete = false,
  onDelete,
  onClick,
  isActive = false,
  isDeleting = false,
  areaGeometryId,
}: ConfigItemProps) => {
  return (
    <div
      className={classNames(styles.configItem, { [styles.active]: isActive })}
      onClick={onClick}
    >
      <p className={styles.configItemName}>{name}</p>
      <div className={styles.configItemActions}>
        {areaGeometryId && (
          <LaunchLink
            to={`/area_geometries/${areaGeometryId}`}
            label={`Open area geometry for ${name} in a new tab`}
            stopClickPropagation
          />
        )}

        {!hideDelete && !isDeleting && (
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onDelete();
            }}
          >
            <TrashCan />
          </button>
        )}
        {isDeleting && <InlineLoading />}
      </div>
    </div>
  );
};

const AreaPanelSkeleton = () => {
  return (
    <div className={styles.areaPanelSkeleton}>
      <SkeletonPlaceholder className={styles.configSkeleton} />
      <SkeletonPlaceholder className={styles.configSkeleton} />

      <div className={styles.searchConfigsContainer}>
        <SkeletonPlaceholder className={styles.searchInputSkeleton} />
        <SkeletonPlaceholder className={styles.addButtonSkeleton} />
      </div>
      <SkeletonPlaceholder className={styles.configSkeleton} />
      <SkeletonPlaceholder className={styles.configSkeleton} />
    </div>
  );
};

export const DynamicTooltipIcon = ({
  children,
  hide,
  tooltipText,
}: {
  children: ReactNode;
  hide: boolean;
  tooltipText: string;
}) => {
  if (hide) return <>{children}</>;

  return (
    <TooltipIcon definition={tooltipText}>
      <>{children}</>
    </TooltipIcon>
  );
};

interface PreventAddNewModalProps {
  onClose: () => void;
  onSave: () => void;
  newConfigId: string | null;
}

const PreventAddNewModal = ({
  onClose,
  onSave,
  newConfigId,
}: PreventAddNewModalProps) => {
  const {
    form,
    discardFormChanges,
    setConfigs,
    selectedConfig,
    resetConfigId,
    baselines,
  } = useScenarioForm();
  return (
    <form.Subscribe
      selector={(state) => {
        return { canSave: state.canSubmit && !!state.values.generalConfig?.id };
      }}
    >
      {({ canSave }) => (
        <Modal
          open
          size="md"
          modalHeading="Save Area Config"
          className={styles.preventAddNewModal}
          primaryButtonText="Save Current Area"
          secondaryButtonText="Discard Changes"
          onRequestClose={onClose}
          onRequestSubmit={onSave}
          onSecondarySubmit={() => {
            onClose();
            // discardFormChanges();

            if (newConfigId) {
              resetConfigId(newConfigId);
            } else {
              discardFormChanges();
            }

            const baselineArea = baselines.areaConfigs;
            if (baselineArea) {
              setConfigs((prev) =>
                prev.map((config) => {
                  return config.id === selectedConfig?.id
                    ? {
                        ...config,
                        areaGeometryId: baselineArea.areaGeometryId,
                        developmentType: baselineArea.developmentType,
                        formation: baselineArea.formation,
                        areaGeometryName: baselineArea.areaGeometryName,
                      }
                    : config;
                })
              );
            }

            const isLocalConfig = selectedConfig?.id === 'local';
            if (isLocalConfig) {
              setConfigs((prev) => prev.filter((c) => c.id !== 'local'));
            }
          }}
          primaryButtonDisabled={!canSave}
          preventCloseOnClickOutside
          closeButtonLabel="Close"
          aria-label="prevent add new modal"
        >
          <p className={styles.description}>
            Save the current Area Config before switching between Areas or all
            unsaved changes will be lost*(required){' '}
          </p>
        </Modal>
      )}
    </form.Subscribe>
  );
};
